// Standard Vietnam Administrative Divisions API Service
// Data source: https://provinces.open-api.vn/api/ (Open API Vietnam - Tổng cục Thống kê GSO)
// Provides 63 provinces, 705 districts, 10,600+ wards with smart caching and offline fallback.
//
// Khi API chậm / lỗi: dùng danh mục tĩnh VIETNAM_DIVISIONS (cùng bộ dữ liệu GSO). Mã của mục
// dự phòng luôn ÂM — không bao giờ trùng mã thật, nên lần tra sau không gọi API với mã giả
// (trước đây mã giả idx+1 khiến API trả nhầm quận/huyện của tỉnh khác, hoặc danh sách rỗng
// cả phiên). Mục dự phòng tra tiếp theo TÊN; kết quả lỗi / rỗng không được lưu cache.

import { removeVietnameseAccents } from './vietnamAddress'
import { VIETNAM_DIVISIONS } from './vietnamDivisions'

export interface ProvinceItem {
  code: number
  name: string
  cleanName: string
  division_type: string
  phone_code?: number
}

export interface DistrictItem {
  code: number
  name: string
  cleanName: string
  division_type: string
  province_code: number
}

export interface WardItem {
  code: number
  name: string
  cleanName: string
  division_type: string
  district_code: number
}

const API_BASE = 'https://provinces.open-api.vn/api/v1'
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000 // 7 days
/** Lần gọi đầu (danh sách tỉnh) hay chậm hơn khi API "ngủ" — cho thêm thời gian. */
const PROVINCES_TIMEOUT_MS = 8000
const DEFAULT_TIMEOUT_MS = 6000

// In-memory cache
const memCache = new Map<string, any>()

// Mã → tên của mọi mục đã trả ra (API, cache hay dự phòng): khi API lỗi giữa chừng vẫn tra
// được danh mục tĩnh theo TÊN, kể cả khi nơi gọi không truyền tên kèm theo.
const provinceNameByCode = new Map<number, string>()
const districtByCode = new Map<number, { name: string; provinceName: string }>()

function rememberProvinces(items: ProvinceItem[]): void {
  for (const p of items) provinceNameByCode.set(p.code, p.name)
}

function rememberDistricts(items: DistrictItem[], provinceName: string): void {
  for (const d of items) districtByCode.set(d.code, { name: d.name, provinceName })
}

function getFromStorage<T>(key: string): T | null {
  if (memCache.has(key)) return memCache.get(key)
  try {
    const raw = localStorage.getItem(`st_geo_${key}`)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (Date.now() - parsed.t > CACHE_TTL_MS) {
      localStorage.removeItem(`st_geo_${key}`)
      return null
    }
    memCache.set(key, parsed.data)
    return parsed.data as T
  } catch {
    return null
  }
}

function saveToStorage<T>(key: string, data: T): void {
  memCache.set(key, data)
  try {
    localStorage.setItem(`st_geo_${key}`, JSON.stringify({ t: Date.now(), data }))
  } catch {
    // quota exceeded or privacy mode
  }
}

async function fetchWithTimeout(url: string, ms = DEFAULT_TIMEOUT_MS): Promise<Response> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), ms)
  try {
    const res = await fetch(url, { headers: { Accept: 'application/json' }, signal: controller.signal })
    return res
  } finally {
    clearTimeout(timer)
  }
}

export function cleanAdminPrefix(name: string): string {
  if (!name) return ''
  return name
    .replace(/^(Thành phố|Tỉnh|Quận|Huyện|Thị xã|Phường|Xã|Thị trấn|TP\.?|Q\.?|H\.?|P\.?|TX\.?|TT\.?)\s*/i, '')
    .trim()
}

// ─── Danh mục tĩnh (dự phòng) ───────────────────────────────────────

const squash = (s: string) => s.replace(/[\s.\-_]/g, '')
/** Khóa so tên: bỏ tiền tố hành chính, bỏ dấu, bỏ khoảng trắng — "TP. Hồ Chí Minh" = "Thành phố Hồ Chí Minh". */
const nameKey = (s: string) => squash(removeVietnameseAccents(cleanAdminPrefix(s)))

const STATIC_PROVINCES = Object.keys(VIETNAM_DIVISIONS)

function staticProvinceKey(provinceName: string): string | undefined {
  const t = nameKey(provinceName)
  if (!t) return undefined
  return (
    STATIC_PROVINCES.find((k) => nameKey(k) === t) ??
    STATIC_PROVINCES.find((k) => {
      const n = nameKey(k)
      return n.length >= 3 && t.length >= 3 && (n.includes(t) || t.includes(n))
    })
  )
}

function staticProvinces(): ProvinceItem[] {
  return STATIC_PROVINCES.map((name, idx) => ({
    code: -(idx + 1),
    name,
    cleanName: cleanAdminPrefix(name),
    division_type: name.startsWith('Thành phố') ? 'thành phố trung ương' : 'tỉnh',
  })).sort((a, b) => a.cleanName.localeCompare(b.cleanName, 'vi'))
}

function districtType(name: string): string {
  if (name.startsWith('Huyện')) return 'huyện'
  if (name.startsWith('Thị xã')) return 'thị xã'
  if (name.startsWith('Thành phố')) return 'thành phố'
  return 'quận'
}

function wardType(name: string): string {
  if (name.startsWith('Xã')) return 'xã'
  if (name.startsWith('Thị trấn')) return 'thị trấn'
  return 'phường'
}

function staticDistricts(provinceCode: number, provinceName: string): DistrictItem[] {
  const key = staticProvinceKey(provinceName)
  if (!key) return []
  const provIdx = STATIC_PROVINCES.indexOf(key)
  const items: DistrictItem[] = Object.keys(VIETNAM_DIVISIONS[key] ?? {}).map((dName, idx) => ({
    code: -((provIdx + 1) * 1000 + idx + 1),
    name: dName,
    cleanName: cleanAdminPrefix(dName),
    division_type: districtType(dName),
    province_code: provinceCode,
  }))
  rememberDistricts(items, key)
  return items.sort((a, b) => a.name.localeCompare(b.name, 'vi'))
}

function staticWards(districtCode: number, districtName: string, provinceName?: string): WardItem[] {
  const target = nameKey(districtName)
  if (!target) return []
  // Có tỉnh thì tìm đúng trong tỉnh đó — "Huyện Châu Thành" có ở nhiều tỉnh
  const provKey = provinceName ? staticProvinceKey(provinceName) : undefined
  const pools = provKey ? [VIETNAM_DIVISIONS[provKey] ?? {}] : Object.values(VIETNAM_DIVISIONS)
  for (const dists of pools) {
    for (const [dName, wards] of Object.entries(dists)) {
      if (nameKey(dName) !== target || !Array.isArray(wards) || wards.length === 0) continue
      return wards
        .map((wName, idx) => ({
          code: -(Math.abs(districtCode) * 1000 + idx + 1),
          name: wName,
          cleanName: cleanAdminPrefix(wName),
          division_type: wardType(wName),
          district_code: districtCode,
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'vi'))
    }
  }
  return []
}

// ─── Tra cứu ────────────────────────────────────────────────────────

/**
 * Lấy danh sách 63 tỉnh thành chuẩn từ provinces.open-api.vn
 */
export async function fetchProvincesApi(): Promise<ProvinceItem[]> {
  const cacheKey = 'provinces_v2'
  const cached = getFromStorage<ProvinceItem[]>(cacheKey)
  if (cached && cached.length > 0) {
    rememberProvinces(cached)
    return cached
  }

  try {
    const res = await fetchWithTimeout(`${API_BASE}/p/`, PROVINCES_TIMEOUT_MS)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const raw = (await res.json()) as any[]
    if (!Array.isArray(raw) || raw.length === 0) throw new Error('empty')
    const items: ProvinceItem[] = raw.map((p) => ({
      code: Number(p.code),
      name: String(p.name),
      cleanName: cleanAdminPrefix(String(p.name)),
      division_type: String(p.division_type || ''),
      phone_code: p.phone_code ? Number(p.phone_code) : undefined,
    }))

    // Sort alphabetically by cleanName
    items.sort((a, b) => a.cleanName.localeCompare(b.cleanName, 'vi'))

    saveToStorage(cacheKey, items)
    rememberProvinces(items)
    return items
  } catch (err) {
    console.warn('[provincesApi] Failed to fetch from API, using fallback data:', err)
    // Không lưu cache — lần tải trang sau thử lại API
    const fallback = staticProvinces()
    rememberProvinces(fallback)
    return fallback
  }
}

/**
 * Lấy danh sách Quận / Huyện của một Tỉnh theo provinceCode
 */
export async function fetchDistrictsApi(provinceCode: number, provinceName?: string): Promise<DistrictItem[]> {
  if (!provinceCode) return []
  let provName = provinceName || provinceNameByCode.get(provinceCode) || ''

  // Mã âm = tỉnh lấy từ danh mục dự phòng → tra tĩnh theo tên, không gọi API với mã giả
  if (provinceCode > 0) {
    const cacheKey = `districts_v2_${provinceCode}`
    const cached = getFromStorage<DistrictItem[]>(cacheKey)
    if (cached && cached.length > 0) {
      rememberDistricts(cached, provName)
      return cached
    }

    try {
      const res = await fetchWithTimeout(`${API_BASE}/p/${provinceCode}?depth=2`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      if (data?.name) provName = provName || String(data.name)
      const raw = (data.districts || []) as any[]
      if (raw.length > 0) {
        const items: DistrictItem[] = raw.map((d) => ({
          code: Number(d.code),
          name: String(d.name),
          cleanName: cleanAdminPrefix(String(d.name)),
          division_type: String(d.division_type || ''),
          province_code: provinceCode,
        }))

        items.sort((a, b) => a.name.localeCompare(b.name, 'vi'))
        saveToStorage(cacheKey, items)
        rememberDistricts(items, provName)
        return items
      }
    } catch (err) {
      console.warn(`[provincesApi] Failed to fetch districts for prov ${provinceCode}:`, err)
    }
  }

  // Fallback to complete VIETNAM_DIVISIONS database (không lưu cache)
  return staticDistricts(provinceCode, provName)
}

/**
 * Lấy danh sách Phường / Xã của một Quận / Huyện theo districtCode
 */
export async function fetchWardsApi(districtCode: number, districtName?: string): Promise<WardItem[]> {
  if (!districtCode) return []
  const known = districtByCode.get(districtCode)
  const distName = districtName || known?.name || ''

  if (districtCode > 0) {
    const cacheKey = `wards_v2_${districtCode}`
    const cached = getFromStorage<WardItem[]>(cacheKey)
    if (cached && cached.length > 0) return cached

    try {
      const res = await fetchWithTimeout(`${API_BASE}/d/${districtCode}?depth=2`)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const raw = (data.wards || []) as any[]
      if (raw.length > 0) {
        const items: WardItem[] = raw.map((w) => ({
          code: Number(w.code),
          name: String(w.name),
          cleanName: cleanAdminPrefix(String(w.name)),
          division_type: String(w.division_type || ''),
          district_code: districtCode,
        }))

        items.sort((a, b) => a.name.localeCompare(b.name, 'vi'))
        saveToStorage(cacheKey, items)
        return items
      }
    } catch (err) {
      console.warn(`[provincesApi] Failed to fetch wards for dist ${districtCode}:`, err)
    }
  }

  // Fallback to complete VIETNAM_DIVISIONS database (không lưu cache)
  return staticWards(districtCode, distName, known?.provinceName)
}

// ─── So khớp tên ────────────────────────────────────────────────────

const NUMERIC = /^\d+$/

function containsWord(haystack: string, needle: string): boolean {
  if (!needle) return false
  const esc = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp(`(^|[^a-z0-9])${esc}([^a-z0-9]|$)`).test(haystack)
}

/**
 * Khớp tên đơn vị hành chính: ĐÚNG TÊN ở bất kỳ vị trí nào thắng mọi khớp gần đúng
 * ("Phường 12" không bị bắt nhầm sang "Phường 1" đứng trước trong danh sách).
 * Tên chỉ là số (Quận 1, Phường 12) chỉ khớp gần đúng khi cả cụm "quận 1" đứng riêng.
 */
function findAdminMatch<T extends { name: string; cleanName: string }>(items: T[], query?: string | null): T | undefined {
  if (!query || !query.trim() || !items.length) return undefined
  const q = query.trim()
  const qLower = q.toLowerCase()
  const qNorm = removeVietnameseAccents(qLower)
  const qCleanNorm = removeVietnameseAccents(cleanAdminPrefix(q).toLowerCase())
  const qNoSpace = squash(qNorm)
  const qCleanNoSpace = squash(qCleanNorm)

  const keyed = items.map((it) => ({
    it,
    norm: removeVietnameseAccents(it.name.toLowerCase()),
    cleanNorm: removeVietnameseAccents(it.cleanName.toLowerCase()),
  }))

  const exact = keyed.find(
    ({ it, norm, cleanNorm }) =>
      it.name.toLowerCase() === qLower ||
      it.cleanName.toLowerCase() === qLower ||
      norm === qNorm ||
      (qCleanNorm && cleanNorm === qCleanNorm) ||
      (qCleanNoSpace && squash(cleanNorm) === qCleanNoSpace) ||
      squash(norm) === qNoSpace,
  )
  if (exact) return exact.it

  return keyed.find(({ norm, cleanNorm }) => {
    if (!cleanNorm) return false
    if (NUMERIC.test(cleanNorm)) return containsWord(qNorm, norm)
    if (NUMERIC.test(qCleanNorm)) return false
    if (qCleanNorm.length >= 2 && (cleanNorm.startsWith(qCleanNorm) || qCleanNorm.startsWith(cleanNorm))) return true
    if (qNorm.includes(cleanNorm) || (qCleanNorm.length >= 2 && cleanNorm.includes(qCleanNorm))) return true
    return false
  })?.it
}

/**
 * Tìm Tỉnh/Thành phù hợp nhất từ chuỗi tên người dùng nhập
 */
export function findProvinceMatch(
  provinces: ProvinceItem[],
  query?: string | null,
): ProvinceItem | undefined {
  return findAdminMatch(provinces, query)
}

/**
 * Tìm Quận/Huyện phù hợp nhất
 */
export function findDistrictMatch(
  districts: DistrictItem[],
  query?: string | null,
): DistrictItem | undefined {
  return findAdminMatch(districts, query)
}

/**
 * Tìm Phường/Xã phù hợp nhất
 */
export function findWardMatch(
  wards: WardItem[],
  query?: string | null,
): WardItem | undefined {
  return findAdminMatch(wards, query)
}

const WARD_LIKE = /^(Phường|Xã|Thị trấn|Đặc khu|P\.|TT\.)\s*/i

/**
 * Khớp tỉnh / quận / phường lấy từ bản đồ (GPS, tìm kiếm) vào danh mục.
 * Địa chỉ theo đơn vị hành chính mới chỉ có 2 cấp (tỉnh → phường/xã, ví dụ "Phường Sài Gòn"):
 * vẫn điền phường/xã, để trống quận/huyện — không đoán quận từ cả chuỗi địa chỉ.
 */
export async function matchAdminDivisions(
  provinces: ProvinceItem[],
  raw: { city?: string | null; district?: string | null; ward?: string | null; fullText?: string | null },
): Promise<{ city: string; district: string; ward: string }> {
  let city = (raw.city || '').trim()
  let district = (raw.district || '').trim()
  let ward = (raw.ward || '').trim()

  // Tên cấp phường/xã nằm ở ô quận/huyện (địa chỉ 2 cấp)
  if (district && WARD_LIKE.test(district)) {
    if (!ward) ward = district
    district = ''
  }

  const prov = findProvinceMatch(provinces, city || raw.fullText)
  if (!prov) return { city, district, ward }
  city = prov.cleanName
  if (!district) return { city, district: '', ward }

  try {
    const dists = await fetchDistrictsApi(prov.code, prov.name)
    const d = findDistrictMatch(dists, district)
    if (d) {
      district = d.name
      if (ward) {
        const wards = await fetchWardsApi(d.code, d.name)
        const w = findWardMatch(wards, ward)
        if (w) ward = w.name
      }
    }
  } catch {
    // giữ tên thô
  }
  return { city, district, ward }
}
